import { Label } from '../../components/ui/label';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Combobox, type ComboboxOption } from '../../components/ui/combobox';
import { MultiSelectCombobox, type MultiSelectComboboxOption } from '../../components/ui/multi-select-combobox';
import { type PipelineNode, type PipelineNodeField } from '../../services/pipelineNode.service';
import { SourceColumnsReorderList } from '../../components/SourceColumnsReorderList';

interface BasicInfoTabProps {
  workingCopy: PipelineNode | null;
  isSourceNode: boolean;
  isFlatFile: boolean;
  tableOptions: ComboboxOption[];
  columnOptions: MultiSelectComboboxOption[];
  lastModifiedColumnOptions: ComboboxOption[];
  availableTablesLoading: boolean;
  sourceColumnsLoading: boolean;
  node?: PipelineNode | null;
  onChange: (updates: Partial<PipelineNode>) => void;
  onFieldsChange?: (fields: PipelineNodeField[]) => void;
  sourceOptions?: ComboboxOption[];
  sourcesLoading?: boolean;
  /** When true, disables name and description editing (used for template-locked entity nodes) */
  templateLocked?: boolean;
}

export function BasicInfoTab({
  workingCopy,
  isSourceNode,
  isFlatFile,
  tableOptions,
  columnOptions,
  lastModifiedColumnOptions,
  availableTablesLoading,
  sourceColumnsLoading,
  node,
  onChange,
  onFieldsChange,
  sourceOptions,
  sourcesLoading,
  templateLocked = false,
}: BasicInfoTabProps) {
  if (!workingCopy) {
    return null;
  }

  return (
    <div className="space-y-6 max-w-2xl">
      <div className="space-y-2">
        <Label htmlFor="node-name">Node Name</Label>
        <Input
          id="node-name"
          value={workingCopy.label || workingCopy.name || ''}
          onChange={(e) => {
            onChange({ label: e.target.value });
          }}
          placeholder="Enter node name"
          disabled={templateLocked}
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="node-type">Node Type</Label>
        <Input
          id="node-type"
          value={workingCopy.node_type || ''}
          disabled
          className="bg-muted"
        />
        <p className="text-xs text-muted-foreground">Node type cannot be changed after creation</p>
      </div>
      <div className="space-y-2">
        <Label htmlFor="node-description">Description</Label>
        <Textarea
          id="node-description"
          value={workingCopy.description || ''}
          onChange={(e) => {
            onChange({ description: e.target.value });
          }}
          placeholder="Enter description"
          rows={4}
          disabled={templateLocked}
        />
      </div>
      {isSourceNode && (
        <>
          {sourceOptions && sourceOptions.length > 0 && (
            <div className="space-y-2">
              <Label htmlFor="source-model">Source</Label>
              <Combobox
                options={sourceOptions}
                value={workingCopy.source_id || ''}
                onValueChange={(value) => {
                  onChange({ source_id: value || undefined });
                }}
                placeholder="Select a source"
                isLoading={sourcesLoading}
                disabled={sourcesLoading}
              />
              <p className="text-xs text-muted-foreground">
                Assign this node to a data source
              </p>
            </div>
          )}
          {!isFlatFile && (
            <div className="space-y-2">
              <Label htmlFor="source-table">Source Table</Label>
              <Combobox
                options={tableOptions}
                value={workingCopy.table_name || ''}
                onValueChange={(value) => {
                  onChange({ table_name: value });
                }}
                placeholder="Select a table"
                isLoading={availableTablesLoading}
                disabled={availableTablesLoading}
              />
              <p className="text-xs text-muted-foreground">
                Select the Snowflake table this node should pull from
              </p>
            </div>
          )}
          {(isFlatFile || workingCopy.table_name) && (
            <>
              {(isFlatFile || node?.table_name) ? (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="composite-key">Composite Key</Label>
                    <MultiSelectCombobox
                      options={columnOptions}
                      value={workingCopy.composite_key || []}
                      onValueChange={(values) => {
                        onChange({ composite_key: values });
                      }}
                      placeholder="Select columns for composite key"
                      isLoading={!isFlatFile && sourceColumnsLoading}
                      disabled={!isFlatFile && sourceColumnsLoading}
                    />
                    <p className="text-xs text-muted-foreground">
                      Select one or more columns that together form a unique identifier for each record
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="last-modified-column">Last Modified Column</Label>
                    <Combobox
                      options={lastModifiedColumnOptions}
                      value={workingCopy.last_modified_column || ''}
                      onValueChange={(value) => {
                        onChange({ last_modified_column: value });
                      }}
                      placeholder="Select last modified column"
                      isLoading={!isFlatFile && sourceColumnsLoading}
                      disabled={!isFlatFile && sourceColumnsLoading}
                    />
                    <p className="text-xs text-muted-foreground">
                      Select a DATE or DATETIME column to track when records were last modified
                    </p>
                  </div>
                  {workingCopy.fields && workingCopy.fields.length > 0 && onFieldsChange && (
                    <div className="space-y-2">
                      <Label>Columns</Label>
                      <p className="text-xs text-muted-foreground mb-3">
                        Drag to reorder columns. The order here determines the column order in the output.
                      </p>
                      <SourceColumnsReorderList
                        fields={workingCopy.fields}
                        onFieldsChange={onFieldsChange}
                      />
                    </div>
                  )}
                </>
              ) : (
                <div className="p-4 bg-muted rounded-md">
                  <p className="text-sm text-muted-foreground">
                    Please save the node with a table selected to configure composite key and last modified column.
                  </p>
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}

